import React from "react";
import { ArrowRight, Link2, Layers } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Reveal, SectionHeading } from "@/components/common";
import { goToAnchor } from "@/lib/nav";
import { PRICING, CURRENCY, SIMPLE_RATE, MILESTONE_RATE } from "@/data/pricing";

const CARDS = [
  { icon: Link2, name: "Simple Payment Link", rate: SIMPLE_RATE, note: "for a successful domestic-card payment" },
  { icon: Layers, name: "Milestone Payments", rate: MILESTONE_RATE, note: "total for a successful domestic-card payment" },
];

export const PricingSection = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-20 sm:py-28 bg-[#f7faf8]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <SectionHeading
            overline="Pricing"
            title="Two ways to get paid."
            subtitle="A clean link for one payment, or full milestones for real projects. Processing powered by Stripe."
            center
          />
        </Reveal>

        <Reveal delay={0.1}>
          <div
            data-testid="pricing-card"
            className="mt-12 rounded-[2rem] bg-[#091630] p-8 sm:p-12 soft-shadow-lg overflow-hidden relative"
          >
            <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-[#21c55e]/20 blur-3xl" />
            <div className="relative grid sm:grid-cols-2 gap-6">
              {CARDS.map((c) => (
                <div key={c.name} className="rounded-2xl bg-white/[0.04] border border-white/10 p-6">
                  <div className="h-11 w-11 rounded-xl bg-[#21c55e]/15 flex items-center justify-center mb-4">
                    <c.icon className="h-5 w-5 text-[#21c55e]" />
                  </div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-300">{c.name}</p>
                  <div className="flex items-end gap-2 mt-2">
                    <span className="font-display text-4xl sm:text-5xl font-extrabold text-white">
                      {c.rate.split(" + ")[0]}
                    </span>
                    <span className="font-display text-2xl font-bold text-slate-300 mb-1">
                      + {CURRENCY} {PRICING.processing.fixed}
                    </span>
                  </div>
                  <p className="text-sm text-slate-300 mt-2">{c.note}</p>
                </div>
              ))}
            </div>

            <div className="relative mt-8 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="pricing-cta-button"
                onClick={() => goToAnchor(navigate, pathname, "#early-access")}
                className="bg-[#21c55e] text-[#091630] font-bold py-3.5 px-7 rounded-full hover:bg-white transition-colors flex items-center justify-center gap-2"
              >
                Create your WorkPay link <ArrowRight className="h-5 w-5" />
              </button>
              <Link
                to="/pricing"
                data-testid="pricing-see-full"
                className="bg-transparent text-white font-semibold py-3.5 px-7 rounded-full border border-white/25 hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
              >
                See full pricing <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="text-xs text-slate-400 text-center mt-6 max-w-2xl mx-auto">
            UAE domestic cards. Simple Payment Links are not free — payment processing always applies.
            International cards and currency conversion may add roughly 1% each. Payments processed through Stripe.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default PricingSection;
